import React, { useRef, useEffect, forwardRef } from 'react'

interface AnimatedCanvasProps {
  className?: string
  onCanvasReady?: (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => void
  'data-component'?: string
  'data-sizing'?: string
}

export const AnimatedCanvas = forwardRef<HTMLCanvasElement, AnimatedCanvasProps>(({
  className = '',
  onCanvasReady,
  'data-component': dataComponent,
  'data-sizing': dataSizing,
}, ref) => {
  const internalRef = useRef<HTMLCanvasElement>(null)
  const canvasRef = (ref as React.RefObject<HTMLCanvasElement>) || internalRef

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    // Set canvas size based on parent container with device pixel ratio support
    const resizeCanvas = () => {
      const container = canvas.parentElement
      if (!container) return
      
      const dpr = window.devicePixelRatio || 1
      const rect = container.getBoundingClientRect()
      
      canvas.width = rect.width * dpr
      canvas.height = rect.height * dpr
      ctx.scale(dpr, dpr)
      canvas.style.width = rect.width + 'px'
      canvas.style.height = rect.height + 'px'
    }

    // Initial size
    resizeCanvas()
    
    // Resize on window resize
    window.addEventListener('resize', resizeCanvas)
    
    // Also use ResizeObserver for container changes
    const resizeObserver = new ResizeObserver(resizeCanvas)
    const container = canvas.parentElement
    if (container) {
      resizeObserver.observe(container)
    }

    // Notify parent component that canvas is ready
    if (onCanvasReady) {
      onCanvasReady(canvas, ctx)
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas)
      resizeObserver.disconnect()
    }
  }, [onCanvasReady, canvasRef])

  return (
    <canvas 
      ref={canvasRef}
      className={`absolute top-0 left-0 ${className}`}
      data-component={dataComponent}
      data-sizing={dataSizing}
    />
  )
})

AnimatedCanvas.displayName = 'AnimatedCanvas' 